import { useState } from 'react';
import {
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import ActivityLogsPanel from './ActivityLogsPanel';
import PrintRecordPage from '../../PrintRecord';

const sections = [
  { key: 'general', label: 'General', icon: Cog6ToothIcon },
  {
    key: 'print',
    label: 'Print Record',
    icon: PrinterIcon,
    children: [
      { key: 'print-collections', label: 'Collections', view: 'collections' },
      { key: 'print-disbursement', label: 'Disbursement', view: 'disbursement' },
      { key: 'print-summary', label: 'Summary', view: 'summary' },
    ],
  },
  { key: 'logs', label: 'Logs', icon: ClipboardDocumentListIcon },
];

const PRINT_VIEW_BY_SECTION = Object.fromEntries(
  sections.find((s) => s.key === 'print').children.map((c) => [c.key, c.view])
);

function SettingsPage() {
  const { role } = useAuth();
  const canViewLogs = role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
  const [activeSection, setActiveSection] = useState('general');
  const [isPrintExpanded, setIsPrintExpanded] = useState(false);
  const visibleSections = sections.filter((s) => s.key !== 'logs' || canViewLogs);

  const handlePrintToggle = () => {
    setIsPrintExpanded((prev) => {
      const next = !prev;
      if (next && !activeSection.startsWith('print-')) {
        setActiveSection('print-summary');
      }
      return next;
    });
  };

  const printView = PRINT_VIEW_BY_SECTION[activeSection];

  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:h-[calc(100dvh-11rem)]">
      <nav className="sm:w-56 shrink-0 bg-white rounded-lg border border-gray-200 p-2 flex flex-wrap sm:flex-col gap-1 overflow-x-auto sm:overflow-y-auto">
        {visibleSections.map((section) =>
          section.children ? (
            <div key={section.key} className="flex flex-wrap sm:flex-col gap-1">
              <button
                type="button"
                onClick={handlePrintToggle}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
                  activeSection.startsWith('print-') && !isPrintExpanded
                    ? 'bg-navy-light text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <section.icon className="h-4 w-4" />
                <span className="flex-1 text-left">{section.label}</span>
                {isPrintExpanded ? (
                  <ChevronDownIcon className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                )}
              </button>

              {isPrintExpanded &&
                section.children.map((child) => (
                  <button
                    key={child.key}
                    type="button"
                    onClick={() => setActiveSection(child.key)}
                    className={`flex items-center rounded-md pl-9 pr-3 py-2 text-sm font-medium whitespace-nowrap transition ${
                      activeSection === child.key ? 'bg-navy-light text-white' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {child.label}
                  </button>
                ))}
            </div>
          ) : (
            <button
              key={section.key}
              type="button"
              onClick={() => setActiveSection(section.key)}
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
                activeSection === section.key ? 'bg-navy-light text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <section.icon className="h-4 w-4" />
              {section.label}
            </button>
          )
        )}
      </nav>

      <div className="flex-1 min-w-0 sm:h-full">
        {activeSection === 'logs' ? (
          <ActivityLogsPanel />
        ) : printView ? (
          <PrintRecordPage view={printView} />
        ) : (
          <section className="bg-white rounded-lg border border-gray-200 p-10 text-center">
            <p className="text-gray-500">General settings — coming soon</p>
          </section>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
